import { useRef, useState, useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import searchQuery from '../../../../recoil/search/atom';
import useDebounce from '../../../../hooks/useDebounce';
import { pushToDataLayer } from '../../../../utils/gtm';
import { GTM_EVENTS } from '../../../../constants/gtmEvents';
import { getSearchResultsCount } from '../../../../recoil/products/selector';

export default function SearchBox({ label, isInDrawer = false }) {
  const [debouncedInputValue, setDebouncedInputValue] = useRecoilState(
    searchQuery
  );
  const searchResultsCount = useRecoilValue(getSearchResultsCount);

  const inputRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const [searchInput, setSearchInput] = useState(debouncedInputValue);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const debounceSearchValue = useDebounce(searchInput, setIsTyping, isMobile);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setDebouncedInputValue(debounceSearchValue);
  }, [debounceSearchValue, setDebouncedInputValue]);

  useEffect(() => {
    setSearchInput(debouncedInputValue);
  }, [debouncedInputValue]);

  useEffect(() => {
    if (!isTyping && isMobile) {
      inputRef.current && inputRef.current.blur();
    }
  }, [isTyping, isMobile]);

  useEffect(() => {
    if (debounceSearchValue && debounceSearchValue.length > 0) {
      pushToDataLayer({
        event: GTM_EVENTS.SEARCH,
        site_search_category: 'Search Bar',
        is_guided_search: 'false',
        site_search_keyword: debounceSearchValue,
        refined_search: 'false',
        site_search_results: searchResultsCount,
        search_term: debounceSearchValue,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultsCount]);

  return (
    <div
      className={`${isInDrawer ? 'search-box-drawer' : 'search-box'} ${
        searchInput ? 'outline' : ''
      }`}
    >
      <input
        type="text"
        name="search"
        value={searchInput}
        ref={inputRef}
        onChange={event => setSearchInput(event.target.value)}
        className="search-input"
        placeholder={label}
        autoComplete="off"
      />

      <button
        className="text-primary search-btn d-flex flex-column align-items-center gap-1 border-0 p-0"
        type="button"
        onMouseEnter={() => inputRef.current.focus()}
      >
        {isTyping && (
          <ArrowPathIcon
            strokeWidth="2"
            className="loading"
            style={{ height: 24, width: 24 }}
          />
        )}

        {!isTyping && !searchInput && (
          <MagnifyingGlassIcon
            strokeWidth="2"
            style={{ height: 24, width: 24 }}
          />
        )}

        {!isTyping && searchInput && (
          <XMarkIcon
            strokeWidth="2"
            style={{ height: 24, width: 24 }}
            onClick={() => setSearchInput('')}
          />
        )}

        <span className="search-label">{label}</span>
      </button>
    </div>
  );
}
